import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  getOrCreateDeviceId,
  generateQR,
  getDealById,
  getQRStatus,
} from '../services/apiService';
import { dealAvailability } from '../services/dealAvailability';
import Logo from '../components/Logo';
import './QRPage.css';

// How often the walker's screen asks whether the shop has scanned the code.
const POLL_MS = 2500;

// Reasons that mean the deal cannot be claimed at all. Being outside the
// shop's opening hours does not block the QR — the walker may be on the way.
const BLOCKING = new Set(['missing', 'ended', 'paused', 'expired', 'scheduled']);

const UNAVAILABLE_TITLES = {
  missing: 'Deal niet meer beschikbaar',
  ended: 'Deal beëindigd',
  paused: 'Deal gepauzeerd',
  expired: 'Deal verlopen',
  scheduled: 'Deal nog niet gestart',
};

function expiresText(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'long',
  }) + ' om 23:59';
}

// qr-status answers with the redemption row; be liberal in what is accepted.
function readQrState(res) {
  const s = String(res?.status || '').toLowerCase();
  if (s === 'redeemed' || res?.redeemed === true || res?.redeemedAt) return 'redeemed';
  if (s === 'expired') return 'expired';
  return 'pending';
}

export default function QRPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [deal, setDeal] = useState(null);
  const [qr, setQr] = useState(null);           // { token, code, expiresAt }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState(null); // { reason, message }
  const [qrState, setQrState] = useState('pending');    // pending | expired
  const redirected = useRef(false);

  // 1. Check the deal is still live in the database, 2. only then claim a code.
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      setUnavailable(null);

      let d = null;
      try {
        d = await getDealById(id);
      } catch (e) {
        // 404 = the deal is gone. Anything else (offline, 500) is not proof the
        // deal ended, so it is reported as a connection problem instead.
        if (e?.status !== 404) {
          if (!cancelled) {
            setError('Kon de deal niet controleren. Controleer je verbinding en probeer opnieuw.');
            setLoading(false);
          }
          return;
        }
      }

      const availability = dealAvailability(d);
      if (BLOCKING.has(availability.reason)) {
        if (!cancelled) {
          setDeal(d);
          setUnavailable(availability);
          setLoading(false);
        }
        return;
      }

      try {
        const qrRes = await generateQR(getOrCreateDeviceId(), id);
        const token = qrRes?.qrToken || qrRes?.token || '';
        // The backend calls the 6-digit code `displayCode`.
        const code = qrRes?.displayCode || qrRes?.code || '';
        if (!cancelled) {
          setDeal(d);
          setQr({ token, code, expiresAt: qrRes?.expiresAt || d?.expiresAt });
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setDeal(d);
          setError(e?.message || 'Kon QR-code niet genereren.');
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id]);

  // 3. Wait for the shop's scan. The moment the backend marks the code as
  //    redeemed, this screen moves on by itself — there is no button for the
  //    walker to press (a manual "ingeleverd" tap could report a redemption
  //    that never happened at the till).
  useEffect(() => {
    if (!qr?.token) return undefined;
    let stopped = false;

    const check = async () => {
      if (stopped || document.visibilityState === 'hidden') return;
      try {
        const state = readQrState(await getQRStatus(qr.token));
        if (stopped) return;
        if (state === 'redeemed' && !redirected.current) {
          redirected.current = true;
          navigate(`/success/${id}`, { replace: true });
        } else if (state === 'expired') {
          setQrState('expired');
        }
      } catch {
        /* network hiccup — try again on the next tick */
      }
    };

    check();
    const t = setInterval(check, POLL_MS);
    document.addEventListener('visibilitychange', check);
    return () => {
      stopped = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', check);
    };
  }, [qr?.token, id, navigate]);

  if (loading) {
    return (
      <div className="qr-404">
        <p>Deal controleren…</p>
      </div>
    );
  }

  if (unavailable) {
    return (
      <div className="qr-404 qr-unavailable" role="alert">
        <div className="qr-unavailable__icon" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </div>
        <h1 className="qr-unavailable__title">
          {UNAVAILABLE_TITLES[unavailable.reason] || 'Deal niet beschikbaar'}
        </h1>
        <p className="qr-unavailable__text">{unavailable.message}</p>
        {deal && (
          <p className="qr-unavailable__deal">{deal.shopName} · {deal.title}</p>
        )}
        <button onClick={() => navigate('/feed')}>Bekijk andere deals</button>
      </div>
    );
  }

  if (error || !qr) {
    return (
      <div className="qr-404">
        <p>{error || 'Code niet gevonden.'}</p>
        <button onClick={() => navigate('/feed')}>Terug naar feed</button>
      </div>
    );
  }

  return (
    <div className="qr">
      <header className="qr__header">
        <button className="qr__back" onClick={() => navigate(-1)} aria-label="Terug">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>
        <Logo size={20} />
        <div className="qr__header-spacer" />
      </header>

      <main className="qr__main">
        <div className="qr__card">
          <div className="qr__visual">
            <div className="qr__bracket qr__bracket--tl" />
            <div className="qr__bracket qr__bracket--tr" />
            <div className="qr__bracket qr__bracket--bl" />
            <div className="qr__bracket qr__bracket--br" />
            <QRCodeSVG
              value={qr.token || qr.code || String(id)}
              size={180}
              level="M"
              marginSize={2}
            />
          </div>

          <div className="qr__label">TOON AAN DE KASSA</div>

          <div className="qr__code-row">
            <div className="qr__code-line" />
            <div className="qr__code">{qr.code || '------'}</div>
            <div className="qr__code-line" />
          </div>

          <div className="qr__info-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4A9EE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="qr__info-icon">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p className="qr__info-text">
              Laat deze code zien aan de medewerker om je deal te verzilveren.
              {qr.expiresAt ? ` Geldig tot ${expiresText(qr.expiresAt)}.` : ''}
            </p>
          </div>
        </div>

        {deal && (
          <div className="qr__deal-summary">
            <div className="qr__deal-info">
              <div className="qr__deal-shop">{deal.shopName}</div>
              <div className="qr__deal-title">{deal.title}</div>
            </div>
          </div>
        )}

        {qrState === 'expired' ? (
          <div className="qr__waiting qr__waiting--expired" role="status">
            Deze code is verlopen.
            <button type="button" onClick={() => window.location.reload()}>
              Nieuwe code
            </button>
          </div>
        ) : (
          <div className="qr__waiting" role="status" aria-live="polite">
            <span className="qr__waiting-dot" aria-hidden="true" />
            <div>
              <div className="qr__waiting-title">Wachten op scan door de winkel…</div>
              <div className="qr__waiting-sub">
                Dit scherm gaat vanzelf verder zodra je code is gescand.
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
